import { useEffect, useRef, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';
import { Image, ProductDraftFormData } from '@anticatrade/shared';
import { API } from '@anticatrade/admin';
import { analytics } from '../../../analytics';

interface DraftAutoSaveOptions {
	api: API;
	isCreation: boolean;
	showTranslations: boolean;
	draftId: number | null;
	setDraftId: (id: number | null) => void;
	setSavingDraft: (saving: boolean) => void;

	// Form state
	title: string | undefined;
	description: string | undefined;
	price: number | undefined;
	conditionID: number | undefined;
	availability: string;
	visible: boolean;
	width: number | undefined;
	length: number | undefined;
	depth: number | undefined;
	height: number | undefined;
	diameter: number | undefined;
	sku: string | undefined;
	shippingZoneA: number | undefined;
	shippingZoneB: number | undefined;
	shippingZoneC: number | undefined;
	shippingZoneD: number | undefined;
	shippingZoneE: number | undefined;
	shippingZoneAOnRequest: boolean;
	shippingZoneBOnRequest: boolean;
	shippingZoneCOnRequest: boolean;
	shippingZoneDOnRequest: boolean;
	shippingZoneEOnRequest: boolean;
	categoryID: number | undefined;
	centuryID: number | undefined;
	styleID: number | undefined;
	authorID: number | undefined;
	authorName: string | undefined;
	originID: number | undefined;
	proposedAuthor: string | undefined;
	materialsIDs: number[] | undefined;
	useOriginalImage: boolean;
	image: Image | undefined;
	originalImage: Image | undefined;
	gallery: Image[] | undefined;
	isProcessingMainImage: boolean;
	galleryProcessingCount: number;
}

const useDraftAutoSave = (o: DraftAutoSaveOptions) => {
	const savingRef = useRef(false);

	const hasContent = useCallback((): boolean => {
		return !!(
			o.title ||
			o.description ||
			o.price != null ||
			o.conditionID != null ||
			o.categoryID != null ||
			o.centuryID != null ||
			o.styleID != null ||
			o.authorID != null ||
			o.originID != null ||
			o.proposedAuthor ||
			o.sku ||
			o.shippingZoneA != null ||
			o.shippingZoneB != null ||
			o.shippingZoneC != null ||
			o.shippingZoneD != null ||
			o.shippingZoneE != null ||
			o.shippingZoneAOnRequest ||
			o.shippingZoneBOnRequest ||
			o.shippingZoneCOnRequest ||
			o.shippingZoneDOnRequest ||
			o.shippingZoneEOnRequest ||
			o.width != null ||
			o.length != null ||
			o.depth != null ||
			o.height != null ||
			o.diameter != null ||
			(o.materialsIDs != null && o.materialsIDs.length > 0) ||
			o.image?.file != null ||
			(o.gallery != null && o.gallery.some((g) => g.file != null))
		);
	}, [
		o.title,
		o.description,
		o.price,
		o.conditionID,
		o.categoryID,
		o.centuryID,
		o.styleID,
		o.authorID,
		o.originID,
		o.proposedAuthor,
		o.sku,
		o.shippingZoneA,
		o.shippingZoneB,
		o.shippingZoneC,
		o.shippingZoneD,
		o.shippingZoneE,
		o.shippingZoneAOnRequest,
		o.shippingZoneBOnRequest,
		o.shippingZoneCOnRequest,
		o.shippingZoneDOnRequest,
		o.shippingZoneEOnRequest,
		o.width,
		o.length,
		o.depth,
		o.height,
		o.diameter,
		o.materialsIDs,
		o.image,
		o.gallery,
	]);

	// Keep the data updated instead of snapshotting it at first
	// render in `saveDraft`.
	const buildFormData = useCallback((): ProductDraftFormData => {
		const data: ProductDraftFormData = {};
		if (o.title) data.title = o.title;
		if (o.description) data.description = o.description;
		if (o.price != null) data.price = o.price;
		if (o.conditionID != null) data.conditionID = o.conditionID;
		if (o.availability !== 'available') data.availability = o.availability;
		if (!o.visible) data.visible = false;
		if (o.width != null) data.width = o.width;
		if (o.length != null) data.length = o.length;
		if (o.depth != null) data.depth = o.depth;
		if (o.height != null) data.height = o.height;
		if (o.diameter != null) data.diameter = o.diameter;
		if (o.sku) data.sku = o.sku;
		if (o.shippingZoneA != null) data.shippingZoneA = o.shippingZoneA;
		if (o.shippingZoneB != null) data.shippingZoneB = o.shippingZoneB;
		if (o.shippingZoneC != null) data.shippingZoneC = o.shippingZoneC;
		if (o.shippingZoneD != null) data.shippingZoneD = o.shippingZoneD;
		if (o.shippingZoneE != null) data.shippingZoneE = o.shippingZoneE;
		if (o.shippingZoneAOnRequest) data.shippingZoneAOnRequest = true;
		if (o.shippingZoneBOnRequest) data.shippingZoneBOnRequest = true;
		if (o.shippingZoneCOnRequest) data.shippingZoneCOnRequest = true;
		if (o.shippingZoneDOnRequest) data.shippingZoneDOnRequest = true;
		if (o.shippingZoneEOnRequest) data.shippingZoneEOnRequest = true;
		if (o.categoryID != null) data.categoryID = o.categoryID;
		if (o.centuryID != null) data.centuryID = o.centuryID;
		if (o.styleID != null) data.styleID = o.styleID;
		if (o.authorID != null) data.authorID = o.authorID;
		if (o.authorName) data.authorName = o.authorName;
		if (o.originID != null) data.originID = o.originID;
		if (o.proposedAuthor) data.proposedAuthor = o.proposedAuthor;
		if (o.materialsIDs != null && o.materialsIDs.length > 0) data.materialsIDs = o.materialsIDs;
		if (o.useOriginalImage) data.useOriginalImage = true;
		data.hasMainImage = !!o.image?.file;
		if (o.originalImage?.file) data.hasOriginalImage = true;

		return data;
	}, [
		o.title,
		o.description,
		o.price,
		o.conditionID,
		o.availability,
		o.visible,
		o.width,
		o.length,
		o.depth,
		o.height,
		o.diameter,
		o.sku,
		o.shippingZoneA,
		o.shippingZoneB,
		o.shippingZoneC,
		o.shippingZoneD,
		o.shippingZoneE,
		o.shippingZoneAOnRequest,
		o.shippingZoneBOnRequest,
		o.shippingZoneCOnRequest,
		o.shippingZoneDOnRequest,
		o.shippingZoneEOnRequest,
		o.categoryID,
		o.centuryID,
		o.styleID,
		o.authorID,
		o.authorName,
		o.originID,
		o.proposedAuthor,
		o.materialsIDs,
		o.useOriginalImage,
		o.image,
		o.originalImage,
	]);

	const shouldBlock = o.isCreation && !o.showTranslations;

	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			shouldBlock && currentLocation.pathname !== nextLocation.pathname && hasContent(),
	);

	useEffect(() => {
		if (blocker.state !== 'blocked') {
			return;
		}
		if (savingRef.current) {
			return;
		}

		// If we're updating an existing draft and images are still
		// loading from S3, just proceed without saving. The draft is
		// already persisted from the previous session.
		if (o.draftId != null && (o.isProcessingMainImage || o.galleryProcessingCount > 0)) {
			blocker.proceed();
			return;
		}

		const saveDraft = async () => {
			savingRef.current = true;
			o.setSavingDraft(true);

			try {
				const formDataJson = JSON.stringify(buildFormData());

				// Collect image files. Always save both versions (bg
				// removed and original) so the merchant can change
				// their mind when returning to the draft.
				const imageFile = o.image?.file;
				const originalImageFile = o.originalImage?.file;

				const galleryFiles: File[] = [];
				if (o.gallery) {
					for (const g of o.gallery) {
						if (g.file) galleryFiles.push(g.file);
					}
				}

				if (o.draftId != null) {
					await o.api.updateProductDraft(
						o.draftId,
						formDataJson,
						imageFile,
						originalImageFile,
						galleryFiles,
					);
				} else {
					const res = await o.api.createProductDraft(
						formDataJson,
						imageFile,
						originalImageFile,
						galleryFiles,
					);
					o.setDraftId(res.id);
					analytics.track('Draft Created');
				}
			} catch (err) {
				console.error('Failed to save draft:', err);
			} finally {
				setTimeout(() => {
					savingRef.current = false;
					o.setSavingDraft(false);
					blocker.proceed();
				}, 300);
			}
		};

		saveDraft();
	}, [blocker.state]);

	// Show browser's native "leave page?" dialog on tab/browser
	// close.
	useEffect(() => {
		if (!shouldBlock) {
			return;
		}
		const handler = (e: BeforeUnloadEvent) => {
			if (hasContent()) {
				e.preventDefault();
				e.returnValue = ''; // For compatibility with older browsers
			}
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [shouldBlock, hasContent]);
};

export { useDraftAutoSave };
